import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import {
  getBookAssemblyJobFailureMessageKey,
  deleteTemporaryAssemblyCover as deleteTemporaryAssemblyCoverClient,
  formatBookAssemblyErrorMessage,
  loadBookAssemblyJob as loadBookAssemblyJobClient,
  loadBookAssemblyJobs as loadBookAssemblyJobsClient,
  loadBookAssemblyLaunchState as loadBookAssemblyLaunchStateClient,
  loadBookAssemblySource as loadBookAssemblySourceClient,
  requestSideStoryPlacementSuggestions as requestSideStoryPlacementSuggestionsClient,
  retryBookExport as retryBookExportClient,
  saveBookAssemblyProjectMetadata as saveBookAssemblyProjectMetadataClient,
  startBookExport as startBookExportClient,
  uploadTemporaryAssemblyCover as uploadTemporaryAssemblyCoverClient,
} from "@/features/book-assembly/api/bookAssemblyClient";
import {
  applySuggestionSetToDraft,
  buildBookExportConfigFromDraft,
  buildBookExportSummaryFromDraft,
  buildBookAssemblyOrderedItems,
  buildChapterSelections,
  buildPlacementChoices,
  createInitialBookAssemblyDraft,
} from "@/features/book-assembly/bookAssemblyMappers";
import type {
  BookAssemblyDraft,
  BookAssemblyLaunchState,
  BookAssemblySourceView,
  BookAssemblyStep,
  BookExportConfig,
  BookExportJob,
  BookExportSettings,
  SideStoryPlacementKind,
} from "@/features/book-assembly/book-assembly.types";
import {
  getLatestBookExportJob,
} from "@/lib/book-assembly/export-report";
import {
  canGenerateBookAssembly,
  createBookAssemblyRunState,
  isBookExportJobActive,
  isBookExportJobStale,
  markBookExportJobAsStaleFailure,
  mapBookExportJobToRunState,
} from "@/lib/book-assembly/export-runner";
import {
  countOrderedItemsByKind,
  moveOrderedItem,
  reorderOrderedItems,
  syncPlacementsFromOrderedItems,
} from "@/lib/book-assembly/manuscript-compiler";
import { subscriptionStore } from "@/stores/subscriptionStore";

export interface BookAssemblyNotice {
  kind: "error" | "success";
  message: string;
}

export interface ProjectBookAssemblyDependencies {
  deleteTemporaryAssemblyCover: typeof deleteTemporaryAssemblyCoverClient;
  loadBookAssemblyJob: typeof loadBookAssemblyJobClient;
  loadBookAssemblyJobs: typeof loadBookAssemblyJobsClient;
  loadBookAssemblyLaunchState: typeof loadBookAssemblyLaunchStateClient;
  loadBookAssemblySource: typeof loadBookAssemblySourceClient;
  requestSideStoryPlacementSuggestions: typeof requestSideStoryPlacementSuggestionsClient;
  retryBookExport: typeof retryBookExportClient;
  saveBookAssemblyProjectMetadata: typeof saveBookAssemblyProjectMetadataClient;
  startBookExport: typeof startBookExportClient;
  uploadTemporaryAssemblyCover: typeof uploadTemporaryAssemblyCoverClient;
}

export interface ProjectBookAssemblyStoreState {
  activeJobId: string | null;
  bookMetadataStatus: "error" | "idle" | "ready" | "saving";
  clearWorkspace: () => Promise<void>;
  currentProjectId: string | null;
  dismissNotice: () => void;
  draft: BookAssemblyDraft | null;
  generateBook: () => Promise<void>;
  hydrateWorkspace: (projectId: string) => Promise<void>;
  isUploadingCover: boolean;
  isPreparingFreshExport: boolean;
  jobs: BookExportJob[];
  jobsStatus: "error" | "idle" | "loading" | "ready";
  launchStates: Record<string, BookAssemblyLaunchState | undefined>;
  loadLaunchState: (projectId: string) => Promise<void>;
  loadJobs: (projectId?: string) => Promise<void>;
  notice: BookAssemblyNotice | null;
  patchExportConfig: (patch: Partial<BookExportConfig>) => void;
  patchSettings: (patch: Partial<BookExportSettings>) => void;
  prepareFreshExport: () => void;
  moveTocItem: (sourceId: string, direction: "down" | "up") => void;
  reorderTocItems: (activeId: string, targetId: string) => void;
  requestSuggestionSet: () => Promise<void>;
  applySuggestionSet: () => void;
  removeUploadedCover: () => Promise<void>;
  retryJob: (jobId: string) => Promise<void>;
  saveBookMetadata: (options?: { silent?: boolean }) => Promise<void>;
  setStep: (step: BookAssemblyStep) => void;
  showNotice: (notice: BookAssemblyNotice) => void;
  source: BookAssemblySourceView | null;
  status: "error" | "idle" | "loading" | "ready";
  suggestionStatus: "error" | "idle" | "loading" | "ready";
  toggleChapter: (chapterId: string, included: boolean) => void;
  updateSideStoryInclusion: (storyId: string, included: boolean) => void;
  updateSideStoryPlacement: (
    storyId: string,
    placementKind: SideStoryPlacementKind,
    anchorChapterId: string | null,
  ) => void;
  uploadCover: (file: File) => Promise<void>;
}

const defaultDependencies: ProjectBookAssemblyDependencies = {
  deleteTemporaryAssemblyCover: deleteTemporaryAssemblyCoverClient,
  loadBookAssemblyJob: loadBookAssemblyJobClient,
  loadBookAssemblyJobs: loadBookAssemblyJobsClient,
  loadBookAssemblyLaunchState: loadBookAssemblyLaunchStateClient,
  loadBookAssemblySource: loadBookAssemblySourceClient,
  requestSideStoryPlacementSuggestions: requestSideStoryPlacementSuggestionsClient,
  retryBookExport: retryBookExportClient,
  saveBookAssemblyProjectMetadata: saveBookAssemblyProjectMetadataClient,
  startBookExport: startBookExportClient,
  uploadTemporaryAssemblyCover: uploadTemporaryAssemblyCoverClient,
};

function getBookAssemblyNoticeMessage(error: unknown) {
  return formatBookAssemblyErrorMessage(error);
}

function rebuildDraft(
  source: BookAssemblySourceView,
  draft: BookAssemblyDraft,
  patch: Partial<BookAssemblyDraft>,
): BookAssemblyDraft {
  const nextDraft = {
    ...draft,
    ...patch,
  };
  const orderedItems = patch.orderedItems
    ?? buildBookAssemblyOrderedItems(source, nextDraft);
  const draftWithOrderedItems = {
    ...nextDraft,
    orderedItems,
  };
  const exportConfig = buildBookExportConfigFromDraft(source, draftWithOrderedItems);

  return {
    ...draftWithOrderedItems,
    exportConfig,
    orderedItems,
    summary: buildBookExportSummaryFromDraft(source, {
      ...draftWithOrderedItems,
      exportConfig,
    }),
  };
}

function updateLaunchState(
  launchStates: ProjectBookAssemblyStoreState["launchStates"],
  projectId: string,
  launchState: BookAssemblyLaunchState,
) {
  return {
    ...launchStates,
    [projectId]: launchState,
  };
}

function mergeBookExportJob(
  jobs: BookExportJob[],
  nextJob: BookExportJob,
) {
  return [nextJob, ...jobs.filter((job) => job.id !== nextJob.id)];
}

function buildLaunchStateFromSourceAndJobs(
  source: BookAssemblySourceView,
  jobs: BookExportJob[],
) {
  return {
    availability: source.availability,
    chapterCount: source.chapters.length,
    href: `/projects/${source.projectId}/stories/book-assembly`,
    latestJobStatus: getLatestBookExportJob(jobs)?.status ?? null,
    recommendedUpgradeTier: source.capabilities.recommendedUpgradeTier,
    sideStoryCount: source.sideStories.length,
    tier: source.tier,
  } satisfies BookAssemblyLaunchState;
}

function syncDraftRunWithJobs(
  draft: BookAssemblyDraft | null,
  jobs: BookExportJob[],
  activeJobId: string | null,
) {
  if (!draft) {
    return draft;
  }

  const activeJob = jobs.find((job) => job.id === activeJobId)
    ?? getLatestBookExportJob(jobs);

  return {
    ...draft,
    run: mapBookExportJobToRunState(activeJob),
    step: activeJob ? "generate" : draft.step,
  };
}

function isBookExportStaleFailure(job: Pick<BookExportJob, "failureCode" | "status">) {
  return job.status === "failed" && job.failureCode === "BOOK_EXPORT_STALE";
}

export function createProjectBookAssemblyStore(
  deps: ProjectBookAssemblyDependencies = defaultDependencies,
) {
  let setStore:
    | ((
      partial:
        | Partial<ProjectBookAssemblyStoreState>
        | ((state: ProjectBookAssemblyStoreState) => Partial<ProjectBookAssemblyStoreState>),
    ) => void)
    | null = null;
  let pollingHandle: ReturnType<typeof setTimeout> | null = null;

  const stopJobPolling = () => {
    if (pollingHandle) {
      clearTimeout(pollingHandle);
      pollingHandle = null;
    }
  };

  const syncLaunchStatesForCurrentProject = (
    state: ProjectBookAssemblyStoreState,
    jobs: BookExportJob[],
  ) => {
    if (!state.currentProjectId || !state.source) {
      return state.launchStates;
    }

    return updateLaunchState(
      state.launchStates,
      state.currentProjectId,
      buildLaunchStateFromSourceAndJobs(state.source, jobs),
    );
  };

  const applyJobsToState = (
    state: ProjectBookAssemblyStoreState,
    jobs: BookExportJob[],
    preferredJobId: string | null = state.activeJobId,
  ) => {
    const effectiveJobs = jobs.map((job) =>
      isBookExportJobStale(job) ? markBookExportJobAsStaleFailure(job) : job
    );
    const resolvedActiveJob = effectiveJobs.find((job) => job.id === preferredJobId)
      ?? getLatestBookExportJob(effectiveJobs);
    const nextActiveJobId = resolvedActiveJob?.id ?? null;

    return {
      activeJobId: nextActiveJobId,
      draft: syncDraftRunWithJobs(state.draft, effectiveJobs, nextActiveJobId),
      jobs: effectiveJobs,
      launchStates: syncLaunchStatesForCurrentProject(state, effectiveJobs),
    };
  };

  const pollJobUntilTerminal = async (
    jobId: string,
    options: { announceTerminal: boolean },
  ) => {
    if (!setStore) {
      return;
    }

    try {
      const loadedJob = await deps.loadBookAssemblyJob(jobId);
      const job = isBookExportJobStale(loadedJob)
        ? markBookExportJobAsStaleFailure(loadedJob)
        : loadedJob;

      setStore((state) => ({
        ...applyJobsToState(state, mergeBookExportJob(state.jobs, job), job.id),
        jobsStatus: "ready",
        notice:
          job.status === "complete" && options.announceTerminal
            ? {
                kind: "success" as const,
                message: "bookAssembly.messages.jobReady",
              }
            : job.status === "failed" && options.announceTerminal
              ? {
                  kind: "error" as const,
                  message: getBookAssemblyJobFailureMessageKey(job),
                }
              : state.notice,
      }));

      if (isBookExportJobActive(job)) {
        stopJobPolling();
        pollingHandle = setTimeout(() => {
          void pollJobUntilTerminal(jobId, options);
        }, 1_500);
        return;
      }

      stopJobPolling();
    } catch (error) {
      stopJobPolling();
      setStore({
        jobsStatus: "error",
        notice: {
          kind: "error",
          message: getBookAssemblyNoticeMessage(error),
        },
      });
    }
  };

  const beginJobPolling = (
    jobId: string,
    options: { announceTerminal: boolean },
  ) => {
    stopJobPolling();
    pollingHandle = setTimeout(() => {
      void pollJobUntilTerminal(jobId, options);
    }, 1_000);
  };

  return createStore<ProjectBookAssemblyStoreState>((set, get) => {
    setStore = set;

    return ({
      activeJobId: null,
      bookMetadataStatus: "idle",
      currentProjectId: null,
      draft: null,
      isUploadingCover: false,
      isPreparingFreshExport: false,
      jobs: [],
    jobsStatus: "idle",
    launchStates: {},
    notice: null,
    source: null,
    status: "idle",
    suggestionStatus: "idle",

    loadLaunchState: async (projectId) => {
      try {
        const launchState = await deps.loadBookAssemblyLaunchState(projectId);
        set((state) => ({
          launchStates: updateLaunchState(state.launchStates, projectId, launchState),
        }));
      } catch {
        set((state) => ({
          launchStates: updateLaunchState(state.launchStates, projectId, {
            availability: {
              code: "project-not-found",
              eligible: false,
              reason: "bookAssembly.errors.notFound",
            },
            chapterCount: 0,
            href: `/projects/${projectId}/stories/book-assembly`,
            recommendedUpgradeTier: null,
            sideStoryCount: 0,
            tier: "free",
          }),
        }));
      }
    },

    loadJobs: async (projectId) => {
      const targetProjectId = projectId ?? get().currentProjectId;
      if (!targetProjectId) {
        return;
      }

      set({
        jobsStatus: "loading",
      });

      try {
        const jobs = await deps.loadBookAssemblyJobs(targetProjectId);
        const activeJob = getLatestBookExportJob(jobs);

        set((state) => ({
          ...applyJobsToState(state, jobs, activeJob?.id ?? null),
          isPreparingFreshExport: false,
          jobsStatus: "ready",
        }));

        if (activeJob && isBookExportJobActive(activeJob)) {
          beginJobPolling(activeJob.id, { announceTerminal: false });
        } else {
          stopJobPolling();
        }
      } catch (error) {
        set({
          jobs: [],
          jobsStatus: "error",
          notice: {
            kind: "error",
            message: getBookAssemblyNoticeMessage(error),
          },
        });
      }
    },

    hydrateWorkspace: async (projectId) => {
      const existingState = get();
      if (
        existingState.currentProjectId === projectId
        && (existingState.status === "loading" || existingState.status === "ready")
      ) {
        return;
      }

      stopJobPolling();
      set({
        bookMetadataStatus: "idle",
        currentProjectId: projectId,
        draft: null,
        notice: null,
        source: null,
        status: "loading",
        suggestionStatus: "idle",
        isPreparingFreshExport: false,
      });

      try {
        const [source, jobs] = await Promise.all([
          deps.loadBookAssemblySource(projectId),
          deps.loadBookAssemblyJobs(projectId).catch(() => []),
        ]);
        const draft = createInitialBookAssemblyDraft(source);
        const activeJob = getLatestBookExportJob(jobs);

        set((state) => ({
          ...applyJobsToState(
            {
              ...state,
              activeJobId: activeJob?.id ?? null,
              currentProjectId: projectId,
              draft,
              source,
            },
            jobs,
            activeJob?.id ?? null,
          ),
          currentProjectId: projectId,
          jobsStatus: "ready",
          isPreparingFreshExport: false,
          notice: null,
          source,
          status: "ready",
          suggestionStatus: "idle",
        }));

        if (activeJob && isBookExportJobActive(activeJob)) {
          beginJobPolling(activeJob.id, { announceTerminal: false });
        }
      } catch (error) {
        set({
          currentProjectId: projectId,
          jobs: [],
          jobsStatus: "error",
          notice: {
            kind: "error",
            message: getBookAssemblyNoticeMessage(error),
          },
          status: "error",
        });
      }
    },

    clearWorkspace: async () => {
      const draft = get().draft;
      stopJobPolling();
      await deps.deleteTemporaryAssemblyCover(draft?.settings.coverAssetPath).catch(() => undefined);

      set({
        activeJobId: null,
        bookMetadataStatus: "idle",
        currentProjectId: null,
        draft: null,
        isUploadingCover: false,
        isPreparingFreshExport: false,
        jobs: [],
        jobsStatus: "idle",
        notice: null,
        source: null,
        status: "idle",
        suggestionStatus: "idle",
      });
    },

    dismissNotice: () => {
      set({
        notice: null,
      });
    },

    showNotice: (notice) => {
      set({
        notice,
      });
    },

    setStep: (step) => {
      set((state) => ({
        draft: state.draft
          ? {
              ...state.draft,
              step,
            }
          : null,
      }));
    },

    toggleChapter: (chapterId, included) => {
      const source = get().source;
      const draft = get().draft;

      if (!source || !draft) {
        return;
      }

      const chapter = source.chapters.find((candidate) => candidate.id === chapterId);
      if (!chapter || chapter.isOpening || chapter.isClosing) {
        return;
      }

      const selectedChapterIds = included
        ? [...new Set([...draft.selectedChapterIds, chapterId])]
        : draft.selectedChapterIds.filter((candidate) => candidate !== chapterId);

      const nextPlacements = draft.sideStoryPlacements.map((placement) => {
        if (
          placement.anchorChapterId === chapterId
          && placement.placementKind !== "appendix"
          && !selectedChapterIds.includes(chapterId)
        ) {
          return {
            ...placement,
            anchorChapterId: null,
          };
        }

        return placement;
      });

      set({
        draft: rebuildDraft(source, draft, {
          selectedChapterIds,
          sideStoryPlacements: nextPlacements,
        }),
      });
    },

    updateSideStoryInclusion: (storyId, included) => {
      const source = get().source;
      const draft = get().draft;
      if (!source || !draft) {
        return;
      }

      const sideStoryPlacements = draft.sideStoryPlacements.map((placement) =>
        placement.storyId === storyId
          ? {
              ...placement,
              included,
            }
          : placement
      );

      set({
        draft: rebuildDraft(source, draft, {
          sideStoryPlacements,
          suggestionSet: draft.suggestionSet,
        }),
      });
    },

    updateSideStoryPlacement: (storyId, placementKind, anchorChapterId) => {
      const source = get().source;
      const draft = get().draft;
      if (!source || !draft) {
        return;
      }

      const normalizedAnchor = placementKind === "appendix" ? null : anchorChapterId;
      const sideStoryPlacements = draft.sideStoryPlacements.map((placement) =>
        placement.storyId === storyId
          ? {
              ...placement,
              anchorChapterId: normalizedAnchor,
              included: true,
              placementKind,
            }
          : placement
      );

      set({
        draft: rebuildDraft(source, draft, {
          sideStoryPlacements,
          suggestionSet: draft.suggestionSet,
        }),
      });
    },

    requestSuggestionSet: async () => {
      const draft = get().draft;
      if (!draft) {
        return;
      }

      set({
        suggestionStatus: "loading",
      });

      try {
        const suggestionSet = await deps.requestSideStoryPlacementSuggestions({
          draft,
        });
        subscriptionStore.getState().setCreditBalance(suggestionSet.creditsRemaining);
        set((state) => ({
          draft: state.draft
            ? {
                ...state.draft,
                suggestionSet,
              }
            : null,
          notice: null,
          suggestionStatus: "ready",
        }));
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: getBookAssemblyNoticeMessage(error),
          },
          suggestionStatus: "error",
        });
        throw error;
      }
    },

    applySuggestionSet: () => {
      const source = get().source;
      const draft = get().draft;
      if (!source || !draft?.suggestionSet) {
        return;
      }

      const nextDraft = applySuggestionSetToDraft(source, draft, draft.suggestionSet);

      set({
        draft: rebuildDraft(source, draft, {
          orderedItems: nextDraft.orderedItems,
          sideStoryPlacements: nextDraft.sideStoryPlacements,
          suggestionSet: nextDraft.suggestionSet,
        }),
      });
    },

    moveTocItem: (sourceId, direction) => {
      const draft = get().draft;
      const source = get().source;
      if (!draft || !source) {
        return;
      }

      const orderedItems = moveOrderedItem(draft.orderedItems, sourceId, direction);
      const sideStoryPlacements = syncPlacementsFromOrderedItems({
        orderedItems,
        placements: draft.sideStoryPlacements,
        selectedChapterIds: draft.selectedChapterIds,
      });

      set({
        draft: rebuildDraft(source, draft, {
          orderedItems,
          sideStoryPlacements,
        }),
      });
    },

    reorderTocItems: (activeId, targetId) => {
      const draft = get().draft;
      const source = get().source;
      if (!draft || !source) {
        return;
      }

      const orderedItems = reorderOrderedItems(draft.orderedItems, activeId, targetId);
      const sideStoryPlacements = syncPlacementsFromOrderedItems({
        orderedItems,
        placements: draft.sideStoryPlacements,
        selectedChapterIds: draft.selectedChapterIds,
      });

      set({
        draft: rebuildDraft(source, draft, {
          orderedItems,
          sideStoryPlacements,
        }),
      });
    },

    patchExportConfig: (patch) => {
      const draft = get().draft;
      const source = get().source;
      if (!draft || !source) {
        return;
      }

      set({
        draft: rebuildDraft(source, draft, {
          exportConfig: {
            ...(draft.exportConfig ?? source.capabilities.defaultConfig),
            ...patch,
          },
          orderedItems: draft.orderedItems,
        }),
      });
    },

    patchSettings: (patch) => {
      const draft = get().draft;
      const source = get().source;
      if (!draft || !source) {
        return;
      }

      set({
        draft: rebuildDraft(source, draft, {
          orderedItems: draft.orderedItems,
          settings: {
            ...draft.settings,
            ...patch,
          },
        }),
      });
    },

    prepareFreshExport: () => {
      set((state) => ({
        activeJobId: null,
        draft: state.draft
          ? {
              ...state.draft,
              run: createBookAssemblyRunState("idle"),
              step: "generate",
            }
          : null,
        isPreparingFreshExport: true,
        notice: null,
      }));
    },

    saveBookMetadata: async (options) => {
      const draft = get().draft;
      const source = get().source;
      if (!draft || !source) {
        return;
      }

      set({
        bookMetadataStatus: "saving",
      });

      try {
        const metadata = await deps.saveBookAssemblyProjectMetadata({
          projectId: draft.projectId,
          settings: {
            authorNote: draft.settings.authorNote,
            bookSubtitle: draft.settings.bookSubtitle,
            dedication: draft.settings.dedication,
            seriesName: draft.settings.seriesName,
            specialThanks: draft.settings.specialThanks,
            volumeLabel: draft.settings.volumeLabel,
          },
        });

        set((state) => {
          if (!state.source || !state.draft) {
            return {
              bookMetadataStatus: "ready" as const,
              notice: options?.silent
                ? state.notice
                : {
                    kind: "success" as const,
                    message: "bookAssembly.messages.metadataSaved",
                  },
            };
          }

          const nextSource: BookAssemblySourceView = {
            ...state.source,
            bookMetadata: {
              ...state.source.bookMetadata,
              author_note_html: metadata.author_note_html,
              dedication_html: metadata.dedication_html,
              special_thanks_html: metadata.special_thanks_html,
            },
            exportIdentity: state.source.exportIdentity
              ? {
                  ...state.source.exportIdentity,
                  series_name: metadata.book_series_name,
                  subtitle: metadata.book_subtitle,
                  volume_label: metadata.book_volume_label,
                }
              : state.source.exportIdentity,
          };

          return {
            bookMetadataStatus: "ready" as const,
            draft: rebuildDraft(nextSource, state.draft, {
              orderedItems: state.draft.orderedItems,
            }),
            notice: options?.silent
              ? state.notice
              : {
                  kind: "success" as const,
                  message: "bookAssembly.messages.metadataSaved",
                },
            source: nextSource,
          };
        });
      } catch (error) {
        set({
          bookMetadataStatus: "error",
          notice: {
            kind: "error",
            message: getBookAssemblyNoticeMessage(error),
          },
        });
        throw error;
      }
    },

    uploadCover: async (file) => {
      const draft = get().draft;
      const source = get().source;
      if (!draft || !source) {
        return;
      }

      set({
        isUploadingCover: true,
      });

      const sessionId = draft.settings.coverAssetPath?.split("/").at(-2)
        ?? draft.projectId
        + "-"
        + Math.random().toString(36).slice(2, 10);

      try {
        await deps.deleteTemporaryAssemblyCover(draft.settings.coverAssetPath).catch(() => undefined);
        const result = await deps.uploadTemporaryAssemblyCover({
          file,
          projectId: draft.projectId,
          sessionId,
        });

        set((state) => ({
          draft: state.draft && state.source
            ? rebuildDraft(state.source, state.draft, {
                orderedItems: state.draft.orderedItems,
                settings: {
                  ...state.draft.settings,
                  coverAssetPath: result.path,
                  coverFileName: result.fileName,
                  coverPreviewUrl: result.previewUrl,
                },
              })
            : null,
          isUploadingCover: false,
        }));
      } catch (error) {
        set({
          isUploadingCover: false,
          notice: {
            kind: "error",
            message: getBookAssemblyNoticeMessage(error),
          },
        });
        throw error;
      }
    },

    removeUploadedCover: async () => {
      const draft = get().draft;
      const source = get().source;
      if (!draft?.settings.coverAssetPath || !source) {
        return;
      }

      await deps.deleteTemporaryAssemblyCover(draft.settings.coverAssetPath).catch(() => undefined);
      set({
        draft: rebuildDraft(source, draft, {
          orderedItems: draft.orderedItems,
          settings: {
            ...draft.settings,
            coverAssetPath: null,
            coverFileName: null,
            coverPreviewUrl: null,
          },
        }),
      });
    },

    generateBook: async () => {
      const draft = get().draft;
      const source = get().source;

      if (!draft || !source || !canGenerateBookAssembly(draft)) {
        return;
      }

      set({
        draft: {
          ...draft,
          run: createBookAssemblyRunState("validating"),
          step: "generate",
        },
        isPreparingFreshExport: false,
        notice: null,
      });

      try {
        await get().saveBookMetadata({ silent: true });
        const started = await deps.startBookExport({
          draft: get().draft ?? draft,
          source: get().source ?? source,
        });
        const currentDraft = get().draft ?? draft;
        const currentSource = get().source ?? source;
        const startedAt = new Date().toISOString();
        const optimisticJob: BookExportJob = {
          artifacts: [],
          completedAt: null,
          config: buildBookExportConfigFromDraft(currentSource, currentDraft),
          currentStage: "validating",
          failureCode: null,
          failureMessage: null,
          id: started.jobId,
          leaseExpiresAt: null,
          manifestSnapshot: null,
          projectId: currentDraft.projectId,
          report: null,
          retryCount: started.retryCount,
          status: "pending",
          updatedAt: startedAt,
        };

        set((state) => ({
          ...applyJobsToState(state, mergeBookExportJob(state.jobs, optimisticJob), optimisticJob.id),
          jobsStatus: "ready",
          notice: {
            kind: "success",
            message: "bookAssembly.messages.jobStarted",
          },
        }));

        beginJobPolling(optimisticJob.id, { announceTerminal: true });
      } catch (error) {
        set((state) => ({
          draft: state.draft
            ? {
                ...state.draft,
                run: createBookAssemblyRunState("failed", {
                  errorCode: error instanceof Error ? error.name : "BOOK_ASSEMBLY_FAILED",
                  errorMessage: getBookAssemblyNoticeMessage(error),
                }),
              }
            : null,
          notice: {
            kind: "error",
            message: getBookAssemblyNoticeMessage(error),
          },
        }));
        throw error;
      }
    },

    retryJob: async (jobId) => {
      const currentProjectId = get().currentProjectId;
      if (!currentProjectId) {
        return;
      }

      const existingJob = get().jobs.find((job) => job.id === jobId);
      if (existingJob && isBookExportStaleFailure(existingJob)) {
        get().prepareFreshExport();
        return;
      }

      set((state) => ({
        ...applyJobsToState(state, state.jobs, jobId),
        isPreparingFreshExport: false,
        notice: null,
      }));

      try {
        const started = await deps.retryBookExport(jobId);
        const restartedAt = new Date().toISOString();
        const retryJob = existingJob
          ? {
              ...existingJob,
              artifacts: [],
              completedAt: null,
              currentStage: "validating" as const,
              failureCode: null,
              failureMessage: null,
              leaseExpiresAt: null,
              report: null,
              retryCount: started.retryCount,
              status: "pending" as const,
              updatedAt: restartedAt,
            }
          : await deps.loadBookAssemblyJob(jobId);

        set((state) => ({
          ...applyJobsToState(state, mergeBookExportJob(state.jobs, retryJob), retryJob.id),
          jobsStatus: "ready",
          notice: {
            kind: "success",
            message: "bookAssembly.messages.jobRestarted",
          },
        }));

        beginJobPolling(retryJob.id, { announceTerminal: true });
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: getBookAssemblyNoticeMessage(error),
          },
        });
        throw error;
      }
    },
  });
  });
}

export const projectBookAssemblyStore = createProjectBookAssemblyStore();

export function useProjectBookAssemblyStore<T>(
  selector: (state: ProjectBookAssemblyStoreState) => T,
) {
  return useStore(projectBookAssemblyStore, selector);
}

export function selectBookAssemblyChapterSelections(
  state: Pick<ProjectBookAssemblyStoreState, "draft" | "source">,
) {
  return state.draft && state.source ? buildChapterSelections(state.source, state.draft) : [];
}

export function selectBookAssemblyPlacementChoices(
  state: Pick<ProjectBookAssemblyStoreState, "draft" | "source">,
) {
  return state.draft && state.source ? buildPlacementChoices(state.source, state.draft) : [];
}

export function selectBookAssemblyCanGenerate(
  state: Pick<ProjectBookAssemblyStoreState, "draft" | "source">,
) {
  return Boolean(
    state.source?.availability.eligible
    && state.source.availability.tierLocked !== true
    && canGenerateBookAssembly(state.draft)
    && state.draft
    && countOrderedItemsByKind(state.draft.orderedItems, "chapter") > 0,
  );
}
